import { DataTableViewOptions } from "@/components/data-table/ColumnVisibilityControl";
import { PaginationControls } from "@/components/data-table/PaginationControls";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import { useNavigate, useSearch } from "@tanstack/react-router";
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable
} from "@tanstack/react-table";
import { useLayoutEffect, useState } from "react";
import { AppTable as AppTableType } from "../../types/app.type";
import AppFilters from "../AppFilters";
import { AppTableBody } from "./AppTableBody";
import { LoadingAppTable } from "./LoadingAppTable";

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  loading: boolean;
}

export function AppTable<TData extends AppTableType, TValue>({
  columns,
  data,
  loading
}: DataTableProps<TData, TValue>) {
  const { filterName } = useSearch({
    from: "/_auth/_fixed-sidebar/_organization-access/organization/$organizationSlug/gallery"
  });
  const navigate = useNavigate();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    state: {
      sorting,
      columnFilters,
      columnVisibility
    }
  });

  const sortingFilteringOrVisibilityInUse =
    sorting.length || columnFilters.length || !table.getIsAllColumnsVisible();

  useLayoutEffect(() => {
    table.getColumn("displayName")?.setFilterValue(filterName);
  }, [filterName, table]);

  return (
    <div>
      <div className="flex justify-between items-center py-4">
        <Input
          name="appSearch"
          placeholder="Search for app..."
          data-testid="appSearch"
          value={filterName ?? ""}
          onChange={(event) =>
            navigate({
              to: "/organization/$organizationSlug/gallery",
              params: (prev) => ({
                organizationSlug: prev.organizationSlug || ""
              }),
              search: (prev) => ({ ...prev, filterName: event.target.value })
            })
          }
          disabled={loading}
          className="max-w-sm"
        />

        <div className="flex items-center gap-2">
          {sortingFilteringOrVisibilityInUse && (
            <Button
              variant="ghost"
              size="sm"
              data-testid="resetFilters"
              onClick={() => {
                setSorting([]);
                setColumnFilters([]);
                setColumnVisibility({});
                navigate({
                  to: "/organization/$organizationSlug/gallery",
                  params: (prev) => ({
                    organizationSlug: prev.organizationSlug || ""
                  })
                });
              }}
            >
              Reset table
            </Button>
          )}
          <AppFilters />
          <DataTableViewOptions table={table} />
        </div>
      </div>

      <div className="rounded border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => {
              return (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHead key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableHeader>
          <TableBody>
            {loading ? <LoadingAppTable /> : <AppTableBody table={table} />}
          </TableBody>
        </Table>
      </div>
      <div className="my-4">
        <PaginationControls table={table} />
      </div>
    </div>
  );
}
